import FileSaver from 'file-saver';
import DataStore, { Query } from 'src/core/stores/data-store';
import i18n from '../i18n';
import moment from 'moment';

export interface OrganizationStatisticsDatatem {
  name: string;
  projects: number;
  surveys: number;
  users: number;
  createdDate: Date;
  updatedDate: Date;
}

export interface OrganizationStatisticsItem {
  items: OrganizationStatisticsDatatem[];
  organizationsCount: number;
  projectsCount: number;
  surveysCount: number;
  usersCount: number;
}

export interface OrganizationStatisticsSummaryItem {
  organizations: number;
  projects: number;
  surveys: number;
  users: number;
}

export interface OrganizationStatisticsCsvReportRequest {
  tenantId?: string;
  startDate?: string;
  endDate?: string;
  mostUsed: boolean;
  headersFirstLine: { key: string; translatedHeader: string }[];
  headersSecondLine: { key: string; translatedHeader: string }[];
  currentLanguage: string;
  csvSeparator: string;
}

export enum CsvFirstLineReportKeys {
  Organizations = 'Organizations',
  Projects = 'Projects',
  Surveys = 'Surveys',
  Users = 'Users',
}

export enum CsvSecondLineReportKeys {
  Organization = 'Organization',
  ProjectsNumber = 'ProjectsNumber',
  UsersNumber = 'UsersNumber',
  SurveysNumber = 'SurveysNumber',
  CreationDate = 'CreationDate',
  LastUpdate = 'LastUpdate',
}

export interface OrganizationStatisticsQuery {
  startDate?: string;
  endDate?: string;
  partnerTenantId?: string;
  mostUsed?: boolean;
}

export interface OrganizationStatisticsState {
  query: OrganizationStatisticsQuery;
  selectorLabel?: string;
}

export interface OrganizationStatisticsChartPoint {
  date: string;
  count: number;
}

export interface OrganizationStatisticsChartData {
  createdOrganizations: OrganizationStatisticsChartPoint[];
  updatedOrganizations: OrganizationStatisticsChartPoint[];
  createdProjects: OrganizationStatisticsChartPoint[];
  updatedProjects: OrganizationStatisticsChartPoint[];
  createdSurveys: OrganizationStatisticsChartPoint[];
  updatedSurveys: OrganizationStatisticsChartPoint[];
  createdUsers: OrganizationStatisticsChartPoint[];
  updatedUsers: OrganizationStatisticsChartPoint[];
}

export interface OrganizationStatisticsChartOrganizationItem {
  updatedProjectsCount: number;
  createdProjectsCount: number;
  updatedSurveysCount: number;
  createdSurveysCount: number;
  date: string;
}
interface OrganizationResultItems {
  id: string;
  name: string;
  createdDate: string;
  updatedDate: string;
  items: OrganizationStatisticsChartOrganizationItem[];
}

interface UserResultItems {
  createdUsersCount: number;
  updatedUsersCount: number;
  date: string;
}

export interface OrganizationStatisticsChartQueryResult {
  organizationResultItems: OrganizationResultItems[];
  userResultItems: UserResultItems[];
}

export enum OrganizationStatisticsChartKey {
  Organizations = 'Organizations',
  Projects = 'Projects',
  Surveys = 'Surveys',
  Users = 'Users',
}

const getChartPointsFromQueryResult = (
  { startDate, endDate }: { startDate?: string; endDate?: string },
  queryResult: OrganizationStatisticsChartQueryResult,
  organizationId?: string
) => {
  const mappedQueryResult = queryResult.organizationResultItems.map((res) => ({
    createdDate: moment(res.createdDate).startOf('D').toISOString(),
    id: res.id,
    items: res.items,
    name: res.name,
    updatedDate: moment(res.updatedDate).startOf('D').toISOString(),
  }));

  const groupedOrgs = Object.values(Object.groupBy(mappedQueryResult, (v) => v.createdDate));
  const createdOrganizationPoints = groupedOrgs.flatMap((org) => {
    if (startDate && endDate) {
      if (startDate.localeCompare(org![0].createdDate) < 0 && endDate.localeCompare(org![0].createdDate) > 0)
        return [{ date: org![0].createdDate, count: org!.length }];
      else return [];
    } else return [{ date: org![0].createdDate, count: org!.length }];
  });

  const updatedOrganizationPoints = groupedOrgs.flatMap((org) => {
    if (startDate && endDate) {
      if (startDate.localeCompare(org![0].updatedDate) < 0 && endDate.localeCompare(org![0].updatedDate) > 0)
        return [{ date: org![0].updatedDate, count: org!.length }];
      else return [];
    } else return [{ date: org![0].updatedDate, count: org!.length }];
  });

  let data: OrganizationStatisticsChartOrganizationItem[] = [];

  if (organizationId) {
    data = mappedQueryResult
      .filter((v) => v.id === organizationId)[0]
      .items.map((item) => ({
        date: moment(item.date).startOf('D').toISOString(),
        createdProjectsCount: item.createdProjectsCount,
        updatedProjectsCount: item.updatedProjectsCount,
        createdSurveysCount: item.createdSurveysCount,
        updatedSurveysCount: item.updatedSurveysCount,
      }));
  } else {
    data = mappedQueryResult.flatMap((data) => {
      const points = [];
      for (const item of data.items) {
        points.push({
          date: moment(item.date).startOf('D').toISOString(),
          createdProjectsCount: item.createdProjectsCount,
          updatedProjectsCount: item.updatedProjectsCount,
          createdSurveysCount: item.createdSurveysCount,
          updatedSurveysCount: item.updatedSurveysCount,
        });
      }
      return points;
    });
  }

  const groupedData = Object.values(Object.groupBy(data, (v) => v.date));
  const dataPoints = [];

  for (const group of groupedData) {
    const reducedGroup = group!.reduce((prev, curr) => {
      return {
        createdProjectsCount: prev.createdProjectsCount + curr.createdProjectsCount,
        createdSurveysCount: prev.createdSurveysCount + curr.createdSurveysCount,
        updatedProjectsCount: prev.updatedProjectsCount + curr.updatedProjectsCount,
        updatedSurveysCount: prev.updatedSurveysCount + curr.updatedSurveysCount,
        date: curr.date,
      };
    });
    dataPoints.push(reducedGroup);
  }

  const createdProjectPoints: OrganizationStatisticsChartPoint[] = [];
  const updatedProjectPoints: OrganizationStatisticsChartPoint[] = [];
  const createdSurveyPoints: OrganizationStatisticsChartPoint[] = [];
  const updatedSurveyPoints: OrganizationStatisticsChartPoint[] = [];

  for (const point of dataPoints) {
    if (point.createdProjectsCount > 0) createdProjectPoints.push({ count: point.createdProjectsCount, date: point.date });
    if (point.updatedProjectsCount > 0) updatedProjectPoints.push({ count: point.updatedProjectsCount, date: point.date });
    if (point.createdSurveysCount > 0) createdSurveyPoints.push({ count: point.createdSurveysCount, date: point.date });
    if (point.updatedSurveysCount > 0) updatedSurveyPoints.push({ count: point.updatedSurveysCount, date: point.date });
  }

  const createdUserPoints = queryResult.userResultItems.flatMap((v) =>
    v.createdUsersCount > 0 ? [{ count: v.createdUsersCount, date: v.date }] : []
  );
  const updatedUserPoints = queryResult.userResultItems.flatMap((v) =>
    v.updatedUsersCount > 0 ? [{ count: v.updatedUsersCount, date: v.date }] : []
  );

  createdOrganizationPoints.sort((a, b) => a.date.localeCompare(b.date));
  updatedOrganizationPoints.sort((a, b) => a.date.localeCompare(b.date));
  createdProjectPoints.sort((a, b) => a.date.localeCompare(b.date));
  updatedProjectPoints.sort((a, b) => a.date.localeCompare(b.date));
  createdSurveyPoints.sort((a, b) => a.date.localeCompare(b.date));
  updatedSurveyPoints.sort((a, b) => a.date.localeCompare(b.date));
  createdUserPoints.sort((a, b) => a.date.localeCompare(b.date));
  updatedUserPoints.sort((a, b) => a.date.localeCompare(b.date));

  const chartData: OrganizationStatisticsChartData = {
    createdOrganizations: createdOrganizationPoints,
    updatedOrganizations: updatedOrganizationPoints,
    createdProjects: createdProjectPoints,
    updatedProjects: updatedProjectPoints,
    createdSurveys: createdSurveyPoints,
    updatedSurveys: updatedSurveyPoints,
    createdUsers: createdUserPoints,
    updatedUsers: updatedUserPoints,
  };

  console.log(chartData);
  return chartData;
};

export class OrganizationStatisticsStore extends DataStore<OrganizationStatisticsDatatem> {
  private summary?: OrganizationStatisticsSummaryItem;
  private reportsUrl: string;
  private chartsUrl: string;
  private chartQueryResult?: OrganizationStatisticsChartQueryResult;
  private organizationList?: { id: string; name: string }[];

  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/organizations/get-statistics`, []);
    this.reportsUrl = `${baseUrl}/identity/api/organizations/export-statistics`;
    this.chartsUrl = `${baseUrl}/identity/api/organizations/get-statistics-charts`;
  }

  public async load(query: Partial<Query>) {
    return await this.handleCallAsync(async () => {
      try {
        let response = await this.httpService.get<OrganizationStatisticsItem>(`${this.baseUrl}?${DataStore.buildUrl(query as Query)}`);
        this._state.set((s) => {
          s.items = response.data.items || [];
          s.count = response.data.organizationsCount || 0;
          return s;
        });
        this.summary = {
          organizations: response.data.organizationsCount,
          projects: response.data.projectsCount,
          surveys: response.data.surveysCount,
          users: response.data.usersCount,
        };
      } catch (e) {
        this._state.set((s) => {
          s.items = [];
          s.count = 0;
          s.errorMessage = `${e}`;
          return s;
        });
        this.summary = {
          organizations: 0,
          projects: 0,
          surveys: 0,
          users: 0,
        };
        return false;
      }

      return true;
    });
  }

  public async exportCsv(item: OrganizationStatisticsCsvReportRequest) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<any, Blob>(`${this.reportsUrl}`, item, { responseType: 'blob' });

      if (result.data == null || result.data.size === 0) {
        throw Error(i18n.t('CrashError'));
      }

      FileSaver.saveAs(result.data, 'organization-statistics.csv');

      return true;
    });
  }

  public getSummary() {
    return this.summary;
  }

  public async getChartData(item?: OrganizationStatisticsQuery, organizationId?: string) {
    if (!this.chartQueryResult) {
      return this.loadCharts(item);
    } else {
      return getChartPointsFromQueryResult({ startDate: item?.startDate, endDate: item?.endDate }, this.chartQueryResult, organizationId);
    }
  }

  public async loadCharts(item?: OrganizationStatisticsQuery, organizationId?: string) {
    try {
      let response = await this.httpService.get<OrganizationStatisticsChartQueryResult>(`${this.chartsUrl}`, item);
      const chartData = response.data;
      this.chartQueryResult = chartData;
      this.organizationList = chartData.organizationResultItems.map((v) => ({ id: v.id, name: v.name }));
      return getChartPointsFromQueryResult({ startDate: item?.startDate, endDate: item?.endDate }, chartData, organizationId);
    } catch (e) {
      return {
        createdOrganizations: [],
        updatedOrganizations: [],
        createdProjects: [],
        updatedProjects: [],
        createdSurveys: [],
        updatedSurveys: [],
        createdUsers: [],
        updatedUsers: [],
      };
    }
  }

  public getOrganizationList() {
    return this.organizationList;
  }
}
